<template>
    <div class="content-wrapper">
        <app-header />

        <menu-mobile />

        <whats-new-modal />

        <main>
            <nuxt />
        </main>

        <app-footer />

        <auth-modal />

        <success-modal />

        <report-comment-modal />

        <donate-modal />

        <portal-target name="modals" multiple />

        <nuxt-link
            :to="localePath('/contact')"
            class="mobile_contact_button btn btn_blue"
        >
            {{ $t('header.contact') }}
        </nuxt-link>

        <div
            v-if="aiChatbotScriptLoaded"
            class="chatbot-icon"
            :class="[aiChatbotOpen && 'chatbot-icon-close']"
            @click="toggleChat"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { aiChatbotKey } from '@/config';
import AuthModal from '@/components/modals/Auth';
import AppHeader from '@/components/layout/Header';
import AppFooter from '@/components/layout/Footer';
import DonateModal from '@/components/modals/Donate';
import SuccessModal from '@/components/modals/Success';
import WhatsNewModal from '@/components/modals/WhatsNew';
import ReportCommentModal from '@/components/modals/ReportComment';
import MenuMobile from '@/components/layout/header-items/MenuMobile';

export default {
    components: {
        AuthModal,
        AppHeader,
        AppFooter,
        MenuMobile,
        DonateModal,
        SuccessModal,
        WhatsNewModal,
        ReportCommentModal
    },

    data() {
        return {
            aiChatbotKey,
            aiChatbotScriptLoaded: false,
            iframeObserver: null
        };
    },

    head() {
        const bodyClasses = [];

        if (this.isScrollingBlocked) {
            bodyClasses.push('notice_open');
        }

        if (!this.$device.isDesktop) {
            bodyClasses.push('mobile');
        }

        return {
            htmlAttrs: {
                dir: ['he', 'ar'].includes(this.$i18n.locale) ? 'rtl' : 'ltr'
            },
            bodyAttrs: {
                class: bodyClasses.join(' ')
            }
        };
    },

    computed: {
        ...mapGetters({
            isMenuOpened: 'isMenuOpened',
            isModalOpen: 'modals/isOpen',
            aiChatbotOpen: 'aiChatbot/isOpen'
        }),

        isScrollingBlocked() {
            return this.isModalOpen || this.isMenuOpened;
        }
    },

    mounted() {
        this.loadChatbotScript();
        this.observeIframe();
    },

    beforeDestroy() {
        if (this.aiChatbotScriptLoaded) {
            const script = document.getElementById('ai-chatbot');

            if (script) {
                script.remove();
            }
        }

        if (this.iframeObserver) {
            this.iframeObserver.disconnect();
        }
    },

    methods: {
        ...mapActions({
            toggleChat: 'aiChatbot/toggleChat'
        }),

        loadChatbotScript() {
            if (!this.aiChatbotKey) {
                return;
            }

            const script = document.createElement('script');

            script.id = 'ai-chatbot';
            script.type = 'module';
            script.src = 'https://noa.cancer.org.il/chat-embed.min.js';
            script.setAttribute('data-token', this.aiChatbotKey);

            script.onload = () => {
                this.aiChatbotScriptLoaded = true;

                window.postMessage('ai_chatbot:open', '*');
            };

            script.onerror = () => {
                this.aiChatbotScriptLoaded = false;
            };

            document.body.appendChild(script);
        },

        observeIframe() {
            const checkIframe = () => {
                const iframe = document.getElementById('ai_chatbot_iframe');

                if (iframe && !this.iframeObserver) {
                    this.setupIframeObserver(iframe);
                } else if (!iframe) {
                    setTimeout(checkIframe, 500);
                }
            };

            checkIframe();
        },

        setupIframeObserver(iframe) {
            this.iframeObserver = new MutationObserver(() => {
                if (!this.aiChatbotOpen) {
                    return;
                }

                const isVisible =
                    iframe.offsetParent !== null &&
                    window.getComputedStyle(iframe).display !== 'none' &&
                    window.getComputedStyle(iframe).visibility !== 'hidden';

                if (!isVisible) {
                    this.toggleChat();
                }
            });

            this.iframeObserver.observe(iframe, {
                attributes: true,
                attributeFilter: ['style', 'class']
            });
        },

        doToggleChat() {
            if (!this.aiChatbotScriptLoaded) {
                return;
            }

            this.toggleChat();
        }
    }
};
</script>

<style lang="scss">
#ai_chatbot_iframe {
    height: 500px;
}

wizychat {
    display: none !important;

    &.chatbot-open {
        display: block !important;
    }

    .wz-fixed-wrapper {
        bottom: 69px !important;
        left: 16px !important;
        max-width: 500px !important;
        max-height: 500px !important;

        div {
            display: none;
        }

        @media (max-width: 35rem) {
            width: 95% !important;
        }
    }
}

.chatbot-icon {
    position: fixed;
    bottom: 20px;
    left: 20px;
    width: 48px;
    height: 48px;
    border-radius: 100%;
    background-color: #ffffff;
    background-image: url('../assets/images/ai_bubble_button.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    cursor: pointer;

    &.chatbot-icon-close {
        background-image: url('../assets/images/ai_bubble_button-close.png');
    }
}

.mobile {
    .chatbot-icon {
        left: 16px !important;
        bottom: 160px !important;
        z-index: 9999999;
    }

    wizychat {
        left: unset !important;
        right: 0 !important;
    }
}
</style>
